/**
 * The guide's section and anchor list, kept apart from the page that renders it.
 *
 * Working pages link into the guide by anchor id, so the ids need to be
 * importable without pulling the whole guide body into those bundles - and a
 * typo in a link becomes a compile error rather than a scroll that goes nowhere.
 */

export const GUIDE_SECTIONS = [
  { id: 'start-here', title: 'Start here', anchors: [] },
  {
    id: 'what-it-measures',
    title: 'What the tool measures',
    anchors: [
      { id: 'measures-cadence', title: 'Where the numbers come from' },
      { id: 'measures-published', title: 'Published vs not published' },
      { id: 'measures-roster', title: 'The Open Scheduling roster' }
    ]
  },
  { id: 'daily-routine', title: 'The daily routine', anchors: [] },
  {
    id: 'dashboard',
    title: 'Dashboard',
    anchors: [
      { id: 'dashboard-coverage', title: 'Patient-visible coverage' },
      { id: 'dashboard-impact', title: 'Patient impact today' },
      { id: 'dashboard-resolution', title: 'Progress toward resolution' },
      { id: 'dashboard-momentum', title: 'Momentum' },
      { id: 'dashboard-deep', title: 'What the deep probe found' },
      { id: 'dashboard-leverage', title: 'Fix once, unblock many' }
    ]
  },
  {
    id: 'departments',
    title: 'Department Work Queue',
    anchors: [
      { id: 'departments-columns', title: 'Reading a row' },
      { id: 'departments-controls', title: 'Views and filters' },
      { id: 'departments-ticket', title: 'Details, notes and tickets' },
      { id: 'departments-statuses', title: 'Work-queue statuses' }
    ]
  },
  {
    id: 'provider-queue',
    title: 'Provider Work Queue',
    anchors: [
      { id: 'queue-defaults', title: 'The default view' },
      { id: 'queue-priority', title: 'Where to start' },
      { id: 'queue-reasons', title: 'Tiers and the blocking reason' },
      { id: 'queue-filters', title: 'Views and column filters' },
      { id: 'queue-history', title: 'History and automatic closure' },
      { id: 'queue-tiles', title: 'Movement tiles' },
      { id: 'queue-rows', title: 'Working a row' }
    ]
  },
  { id: 'levels', title: 'Level 1 vs Level 2', anchors: [] },
  { id: 'priority', title: 'Priority tiers', anchors: [] },
  { id: 'faq', title: 'Common questions', anchors: [] }
] as const;

type Section = (typeof GUIDE_SECTIONS)[number];
type Anchor = Section['anchors'][number];

export type GuideSectionId = Section['id'];

/** Anything a page is allowed to link to: a whole section, or a heading inside one. */
export type GuideTarget = GuideSectionId | Anchor['id'];

export const GUIDE_TARGET_TITLES: Record<string, string> = GUIDE_SECTIONS.reduce(
  (titles, section) => {
    titles[section.id] = section.title;
    section.anchors.forEach((anchor: { id: string; title: string }) => {
      titles[anchor.id] = `${section.title} - ${anchor.title}`;
    });
    return titles;
  },
  {} as Record<string, string>
);
